import { COPY_LONG, COPY_SHORT } from './plan';

/*
 * A still picture for a slot (เทมเพลตวิดีโอ ข้อ ข): the photo from the phone, made no bigger than a clip copy
 * (1080 × 1920 or 1920 × 1080) and kept as JPEG on this device like the clips (src/video/media.ts, kind 'photo').
 * On screen and in the export it fills the frame and zooms in slowly (PHOTO_ZOOM, src/video/project.ts).
 */

export interface PreparedPhoto { blob: Blob; width: number; height: number; thumb: string }

const toBlob = (c: HTMLCanvasElement, q: number) =>
  new Promise<Blob>((res, rej) => c.toBlob(b => b ? res(b) : rej(new Error('แปลงรูปไม่สำเร็จ (หน่วยความจำไม่พอ)')), 'image/jpeg', q));

/**
 * read a picture file and shrink it; throws with a Thai reason (e.g. a format this browser cannot open).
 * Decoded through an <img> (turned the way the phone took it — EXIF orientation — like on screen) and drawn straight
 * at the smaller size, rather than createImageBitmap with options some Safari versions do not know.
 */
export async function preparePhoto(file: Blob, name: string): Promise<PreparedPhoto> {
  const url = URL.createObjectURL(file);
  const img = new Image();
  try {
    img.src = url;
    try { await img.decode(); }
    catch { throw new Error(`เปิดรูป "${name}" ไม่ได้ (เบราว์เซอร์นี้อาจไม่รู้จักชนิดไฟล์นี้) ลองบันทึกรูปเป็น JPG หรือ PNG แล้วเลือกใหม่`); }
    const iw = img.naturalWidth, ih = img.naturalHeight;
    if (!iw || !ih) throw new Error(`รูป "${name}" ไม่มีขนาด เปิดไม่ได้`);
    const long = Math.max(iw, ih), short = Math.min(iw, ih);
    const s = Math.min(1, COPY_LONG / long, COPY_SHORT / short);
    const w = Math.max(2, Math.round(iw * s)), h = Math.max(2, Math.round(ih * s));
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const g = c.getContext('2d');
    if (!g) throw new Error('เครื่องนี้วาดรูปไม่ได้ (หน่วยความจำไม่พอ)');
    g.drawImage(img, 0, 0, w, h);
    const blob = await toBlob(c, 0.9);
    const t = document.createElement('canvas');
    const ts = 112 / Math.max(w, h);
    t.width = Math.max(1, Math.round(w * ts)); t.height = Math.max(1, Math.round(h * ts));
    t.getContext('2d')?.drawImage(c, 0, 0, t.width, t.height);
    const thumb = t.toDataURL('image/jpeg', 0.7);
    c.width = c.height = 0;
    return { blob, width: w, height: h, thumb };
  } finally {
    img.removeAttribute('src');
    URL.revokeObjectURL(url);
  }
}

/** a picture drawn to fill a w × h frame (centre), zoomed by `zoom` (1 = fills exactly) */
export function drawCover(g: CanvasRenderingContext2D, src: CanvasImageSource & { width: number; height: number }, w: number, h: number, zoom = 1) {
  const s = Math.max(w / src.width, h / src.height) * zoom;
  const dw = src.width * s, dh = src.height * s;
  g.drawImage(src, (w - dw) / 2, (h - dh) / 2, dw, dh);
}
